import {
  AccountError,
  type AccountActivationRequest,
  type AccountAddress,
  type AccountAddressInput,
  type AccountErrorKind,
  type AccountOrderSummary,
  type AccountProfile,
  type CollectionPoint,
  type EmailChangeRequest,
  type OrderReturn,
  type PasswordResetRequest,
  type ProductReviewPage,
  type ReturnRequestInput,
  type ReviewReceipt,
  type ReviewSubmission,
  type SignInRequest,
} from '@akademi/site-builder-runtime';

export interface SavedListLine {
  productId: number;
  quantity: DecimalString;
}

export interface SavedList {
  id: number;
  token?: string | null;
  name: string;
  lines: SavedListLine[];
}

export interface Quote {
  id: number;
  quoteNumber: string;
  revision: number;
  status: string;
  currency: string;
  expiresAt?: string | null;
  subtotal: DecimalString;
  discountTotal: DecimalString;
  shippingTotal: DecimalString;
  taxTotal: DecimalString;
  total: DecimalString;
  lines: unknown[];
}

export interface SavedListsSource {
  listSavedLists(): Promise<SavedList[]>;
  renameSavedList(id: number, name: string): Promise<SavedList>;
  removeSavedListLine(id: number, productId: number): Promise<SavedList>;
  addSavedListToCart(id: number, cartToken: string): Promise<Cart>;
  createSavedList(name: string): Promise<SavedList>;
}

/**
 * Trade accounts: applying for one, and where the shopper's application stands. Refusals are
 * {@link AccountError}s by kind — `APPLICATION_OPEN`, `ALREADY_TRADE`, `INVALID_INPUT`,
 * `RATE_LIMITED`, and `NOT_FOUND` from a shop that takes no applications.
 */
export interface TradeApplicationSource {
  /** Whether this shop takes applications: its site says so. Off when it does not say, or cannot be read. */
  tradeApplicationsEnabled(): Promise<boolean>;
  /** With documents, a multipart form; without, JSON. Documents refused are `INVALID_FILE`. */
  submitTradeApplication(input: TradeApplicationRequest, documents?: TradeDocumentUpload[]): Promise<TradeApplicationStatus>;
  /** What this shop asks of an applicant, so the form can list it before they start. */
  tradeApplicationRequirements(): Promise<TradeRequirements>;
  /** Null when the shopper has none, and when nobody is signed in. Read from the profile's `trade`. */
  myTradeApplication(): Promise<TradeApplicationStatus | null>;
  /** The approved trade account the shopper is a user of; null when none, or nobody is signed in. */
  myTradeAccount(): Promise<TradeAccountSummary | null>;
  /** The proof documents an application made at sign-up still lacks, as the signed-in shopper. */
  uploadTradeDocuments(documents: TradeDocumentUpload[]): Promise<TradeApplicationStatus>;
}

/**
 * The shop's installers: applying, the documents page reached from a signed link, and a signed-in
 * installer's areas, jobs and quotes. Refusals are {@link AccountError}s by kind, the backend's
 * `developerMessage`: `APPLICATION_OPEN`, `ALREADY_INSTALLER`, `NO_AGREEMENT`, `AGREEMENT_CHANGED`,
 * `INVALID_LINK`, `NOT_INSTALLER`, `CONFLICT`, `INVALID_INPUT`, `INVALID_FILE`, `NOT_FOUND`.
 */
export type InstallerSource = InstallerApplicationMethods & InstallerSelfMethods & InstallerMeMethods;

/** `/account/me` as this app answers it: the shopper, and the standing commerce adds to the profile. */
interface StandingProfile {
  email: string;
  firstName: string | null;
  lastName: string | null;
  trade?: Record<string, unknown> | null;
  installer?: Record<string, unknown> | null;
}
import type {
  BrandCard,
  Cart,
  CartAddition,
  CartFulfilment,
  CategoryNode,
  CheckoutRequest,
  CommerceSource,
  DecimalString,
  Order,
  OrderLookup,
  PaymentOptions,
  PaymentRequest,
  PaymentResult,
  ProductDetail,
  ProductListing,
  ProductListingQuery,
} from '@akademi/site-builder-runtime';
import type { NewAccountRequest, SignUpOutcome } from '@commerce/site-builder-blocks';
import type {
  TradeAccountSummary,
  TradeApplicationRequest,
  TradeApplicationStatus,
  TradeDocumentUpload,
  TradeRequirements,
} from '@commerce/site-builder-blocks';
import type {
  InstallerApplicationMethods,
  InstallerApplicationRequest,
  InstallerApplicationStatus,
  InstallerCertificateUpload,
  InstallerJob,
  InstallerMe,
  InstallerMeMethods,
  InstallerPolicyInput,
  InstallerQuoteRequest,
  InstallerRequirements,
  InstallerSelfMethods,
  InstallerSelfView,
  InstallerSigning,
} from '@commerce/site-builder-blocks';
import { listingQueryToParams } from './listingQuery';
import { createBuyingListSource, type BuyingListSource } from './buyingLists';

/**
 * The host adapter blocks read the catalogue through.
 *
 * <h3>Why it talks to this app and not to the backend</h3>
 *
 * <p>Every call below goes to this app's own `/api/storefront/**` handlers,
 * which add `?domain=<host>` on the server from the request's own `Host`
 * header. The browser therefore names no tenant, no domain and no channel — it
 * asks "the catalogue for the site I am on", and the server is the only thing
 * that knows which site that is. An anonymous visitor able to supply a tenant
 * id would be a tenant-isolation hole, not a convenience, and routing through
 * the BFF is what makes that structurally impossible rather than merely
 * discouraged.
 *
 * <p>It also keeps the backend's address out of the client bundle: `SORSY_API_URL`
 * is a server-only variable and is never inlined into shipped JavaScript.
 *
 * <h3>Failure behaviour</h3>
 *
 * <p>An empty result set is a result: `listProducts` returns `items: []` and
 * `total: 0` rather than throwing, so a filter that matches nothing renders an
 * empty grid with its facets intact instead of an error. A transport or server
 * failure DOES throw — the consuming block catches it and renders its own
 * placeholder, which is diagnosable, where a silent empty grid is not.
 */
export interface StorefrontCommerceSourceConfig {
  /** The app's base path, when it is deployed under one. */
  basePath?: string;
  /** Active BCP 47 tag, resolved server-side and passed down. Locale is not a
   *  tenant selector, so the browser may carry it; the tenant never is. */
  locale?: string;
  /** Set by the host auth boundary and update on sign-in, sign-out, or account switch. */
  buyingListsScopeKey?: string;
}

class StorefrontCommerceError extends Error {
  constructor(path: string, status: number) {
    super(`storefront request failed: ${path} (${status})`);
    this.name = 'StorefrontCommerceError';
  }
}

export function createStorefrontCommerceSource(
  config: StorefrontCommerceSourceConfig = {},
): CommerceSource & SavedListsSource & TradeApplicationSource & InstallerSource & { buyingLists?: BuyingListSource } {
  const base = `${(config.basePath ?? '').replace(/\/+$/, '')}/api/storefront`;

  async function get<T>(path: string, params?: URLSearchParams): Promise<T | null> {
    const search = new URLSearchParams(params);
    if (config.locale) search.set('locale', config.locale);
    const qs = search.toString();
    const res = await fetch(`${base}${path}${qs ? `?${qs}` : ''}`, {
      headers: { Accept: 'application/json' },
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new StorefrontCommerceError(path, res.status);
    return (await res.json()) as T;
  }

  /** `get`, but the status comes back too — see `getCart`. */
  async function getRaw<T>(path: string): Promise<{ status: number; body: T | null }> {
    const search = new URLSearchParams();
    if (config.locale) search.set('locale', config.locale);
    const qs = search.toString();
    const res = await fetch(`${base}${path}${qs ? `?${qs}` : ''}`, {
      headers: { Accept: 'application/json' },
    });
    if (res.status === 404 || res.status === 422) return { status: res.status, body: null };
    if (!res.ok) throw new StorefrontCommerceError(path, res.status);
    return { status: res.status, body: (await res.json()) as T };
  }

  /**
   * A cart mutation.
   *
   * <p>Unlike `get`, a 404 here THROWS rather than returning null. A missing
   * basket during a write is not an empty result — it means the token is dead,
   * and silently resolving would leave the shopper clicking Add with nothing
   * happening. Only `getCart` treats absence as an answer.
   */
  const SAVED_LIST_TOKEN_KEY = 'sorsy.saved-list.token';

  function savedListToken(): string | null {
    try {
      return window.localStorage.getItem(SAVED_LIST_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  function rememberSavedListToken(value: string | null | undefined): void {
    if (!value) return;
    try {
      window.localStorage.setItem(SAVED_LIST_TOKEN_KEY, value);
    } catch {
      // A blocked storage area must not make the list API unusable.
    }
  }

  function savedListHeaders(): Record<string, string> {
    const token = savedListToken();
    return token ? { 'X-Saved-List-Token': token } : {};
  }

  async function savedGet<T>(path: string): Promise<T> {
    const res = await fetch(`${base}${path}`, {
      headers: { Accept: 'application/json', ...savedListHeaders() },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new StorefrontCommerceError(path, res.status);
    return (await res.json()) as T;
  }

  async function savedSend<T>(path: string, method: string, body?: unknown): Promise<T> {
    const res = await fetch(`${base}${path}`, {
      method,
      headers: {
        Accept: 'application/json',
        ...savedListHeaders(),
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
    if (!res.ok) throw new StorefrontCommerceError(path, res.status);
    return (await res.json()) as T;
  }

  async function send<T>(
    path: string,
    method: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const search = new URLSearchParams();
    if (config.locale) search.set('locale', config.locale);
    const qs = search.toString();
    const res = await fetch(`${base}${path}${qs ? `?${qs}` : ''}`, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(extraHeaders ?? {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) throw new StorefrontCommerceError(path, res.status);
    return (await res.json()) as T;
  }

  /**
   * An account call: JSON in, JSON out, and a refusal as an {@link AccountError}.
   *
   * <p>The kind comes from the backend's `developerMessage` (or `code`, for the
   * rate limiter's body), so a form picks a translated sentence by code rather
   * than showing whatever English the server wrote.
   */
  async function account<T>(
    path: string,
    method: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          ...(body === undefined || body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
          ...(extraHeaders ?? {}),
        },
        body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
        credentials: 'same-origin',
      });
    } catch {
      throw new AccountError('UNAVAILABLE', 0);
    }
    if (res.ok) {
      const text = await res.text();
      return (text ? JSON.parse(text) : null) as T;
    }
    const payload = (await res.json().catch(() => ({}))) as {
      developerMessage?: string;
      code?: string | number;
      message?: string;
    };
    throw new AccountError(accountErrorKind(res.status, payload), res.status, payload.message);
  }

  /** A key unique to one payment attempt. `randomUUID` where it exists — every
   *  browser this app supports — with a timestamp fallback so a page served over
   *  plain HTTP in development still gets distinct keys. */
  function attemptKey(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
    return `attempt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  /**
   * A sign-up with its password. Answers whether the account opened at once, with this browser
   * signed in, or waits for its emailed code; nothing else of the server's answer is kept.
   */
  async function register(request: NewAccountRequest): Promise<SignUpOutcome> {
    const answer = await account<Partial<SignUpOutcome> | null>('/account/register', 'POST', request);
    const activationRequired = answer?.activationRequired !== false;
    return { activationRequired, signedIn: !activationRequired && answer?.signedIn === true };
  }

  /**
   * Whether the shop takes trade applications, read once from its site descriptor. Anything but
   * an explicit `tradeApplications: true` is off; a failed read is off too, and read again next
   * time rather than remembered.
   */
  let tradeApplicationsFlag: Promise<boolean> | null = null;

  function tradeApplicationsEnabled(): Promise<boolean> {
    tradeApplicationsFlag ??= get<{ tradeApplications?: boolean | null }>('/site').then(
      (site) => site?.tradeApplications === true,
      () => {
        tradeApplicationsFlag = null;
        return false;
      },
    );
    return tradeApplicationsFlag;
  }

  /** `?language=en|tr` for the installer calls whose answer carries the agreement's words. */
  function languageQuery(): string {
    const language = (config.locale ?? '').slice(0, 2).toLowerCase();
    return language === 'en' || language === 'tr' ? `?language=${language}` : '';
  }

  /**
   * The documents page's link token, as the header the BFF forwards. It never goes in a URL: a
   * URL reaches access logs and `Referer`s, and the token opens the installer's documents.
   */
  function linkHeader(token: string): Record<string, string> {
    return { 'X-Installer-Link': token };
  }

  /**
   * The signed-in shopper's profile as `/account/me` answers it — names, and the `trade` and
   * `installer` standing the backend adds (contract §2, §5) — or null when nobody is signed in.
   *
   * <p>One read serves every caller that asks while it is in flight: the account page asks for
   * the trade account, the application and the installer standing together, and they are one
   * profile.
   */
  let standingRead: Promise<StandingProfile | null> | null = null;

  function standing(): Promise<StandingProfile | null> {
    standingRead ??= (async () => {
      try {
        return (await account<StandingProfile | null>('/account/me', 'GET')) ?? null;
      } catch (error) {
        if (error instanceof AccountError && error.kind === 'NOT_SIGNED_IN') return null;
        throw error;
      }
    })().finally(() => {
      standingRead = null;
    });
    return standingRead;
  }

  /** Files as a multipart form, each part named `<prefix><type>`. The browser writes the boundary. */
  function filesForm(files: Array<{ type: string; file: File }>, prefix: string): FormData {
    const form = new FormData();
    for (const { type, file } of files) form.append(`${prefix}${type}`, file, file.name);
    return form;
  }

  return {
    ...(config.buyingListsScopeKey ? { buyingLists: createBuyingListSource({ basePath: config.basePath, scopeKey: config.buyingListsScopeKey }) } : {}),
    async listProducts(query: ProductListingQuery = {}): Promise<ProductListing> {
      const listing = await get<ProductListing>('/listing', listingQueryToParams(query));
      return (
        listing ?? {
          items: [],
          total: 0,
          page: query.page ?? 0,
          size: query.size ?? 0,
          facets: [],
        }
      );
    },

    async getProductBySlug(slug: string): Promise<ProductDetail | null> {
      // A moved, merged or kit slug comes back as a BODY carrying `redirectTo`
      // rather than as an HTTP redirect: the Location is a path on THIS site,
      // and letting `fetch` follow it would send the browser's XHR to a page
      // instead of handing the block a destination to link to.
      return get<ProductDetail>(`/product/${encodeURIComponent(slug)}`);
    },

    async listCategories(): Promise<CategoryNode[]> {
      return (await get<CategoryNode[]>('/categories')) ?? [];
    },

    async listBrands(): Promise<BrandCard[]> {
      return (await get<BrandCard[]>('/brands')) ?? [];
    },

    // ── Cart ────────────────────────────────────────────────────────────────

    async createCart(): Promise<Cart> {
      return send<Cart>('/cart', 'POST', {});
    },

    async getCart(token: string): Promise<Cart | null> {
      // Null rather than throwing is the point: a shopper returning to a dead
      // token should get an empty basket, not an error page.
      //
      // TWO statuses mean "there is no live basket here", and both must map to
      // null or the token is never cleared:
      //   404  the basket was never created, or has been swept
      //   422  `basket is not active` — it was checked out, in this tab or
      //        another one. The backend keeps the row (it is now an order's
      //        source) and refuses to serve it as a basket, which is correct
      //        for a mutation and useless for a read.
      // Anything else is a real failure and still throws.
      const res = await getRaw<Cart>(`/cart/${encodeURIComponent(token)}`);
      return res.status === 404 || res.status === 422 ? null : res.body;
    },

    async addToCart(token: string, item: CartAddition): Promise<Cart> {
      return send<Cart>(`/cart/${encodeURIComponent(token)}/items`, 'POST', item);
    },

    async setCartLineQuantity(token: string, lineId: number, quantity: DecimalString): Promise<Cart> {
      return send<Cart>(
        `/cart/${encodeURIComponent(token)}/items/${lineId}`, 'PATCH', { quantity },
      );
    },

    async removeCartLine(token: string, lineId: number): Promise<Cart> {
      return send<Cart>(`/cart/${encodeURIComponent(token)}/items/${lineId}`, 'DELETE');
    },

    // ── Saved lists ──────────────────────────────────────────────────────────

    async listSavedLists(): Promise<SavedList[]> {
      const lists = await savedGet<SavedList[]>('/saved-lists');
      lists.forEach((list) => rememberSavedListToken(list.token));
      return lists;
    },

    async renameSavedList(id: number, name: string): Promise<SavedList> {
      const list = await savedSend<SavedList>(`/saved-lists/${id}`, 'PATCH', { name });
      rememberSavedListToken(list.token);
      return list;
    },

    async removeSavedListLine(id: number, productId: number): Promise<SavedList> {
      const current = await savedGet<SavedList>(`/saved-lists/${id}`);
      const list = await savedSend<SavedList>(`/saved-lists/${id}/lines`, 'PUT', {
        lines: current.lines.filter((line) => line.productId !== productId),
      });
      rememberSavedListToken(list.token);
      return list;
    },

    async addSavedListToCart(id: number, cartToken: string): Promise<Cart> {
      return savedSend<Cart>(`/saved-lists/${id}/to-cart`, 'POST', { cartToken });
    },

    async createSavedList(name: string): Promise<SavedList> {
      const list = await savedSend<SavedList>('/saved-lists', 'POST', { name });
      rememberSavedListToken(list.token);
      return list;
    },

    async applyCoupon(token: string, code: string): Promise<Cart> {
      return send<Cart>(`/cart/${encodeURIComponent(token)}/coupon`, 'POST', { code });
    },

    async removeCoupon(token: string): Promise<Cart> {
      return send<Cart>(`/cart/${encodeURIComponent(token)}/coupon`, 'DELETE');
    },

    async setCartFulfilment(token: string, fulfilment: CartFulfilment): Promise<Cart> {
      return send<Cart>(`/cart/${encodeURIComponent(token)}/fulfilment`, 'PUT', fulfilment);
    },

    async checkout(request: CheckoutRequest): Promise<Order> {
      return send<Order>('/checkout', 'POST', request);
    },

    async payForOrder(request: PaymentRequest): Promise<PaymentResult> {
      // A fresh idempotency key per attempt, generated here rather than server-side:
      // a retry after a decline must be a NEW attempt, while a double-click or a
      // dropped connection inside one attempt must not be. Only the browser knows
      // which of those it is doing.
      return send<PaymentResult>('/payments', 'POST', request, {
        'Idempotency-Key': attemptKey(),
      });
    },

    async paymentOptions(): Promise<PaymentOptions | null> {
      return get<PaymentOptions>('/payments/options');
    },

    async paymentStatus(
      orderNumber: string,
      proof: OrderLookup,
    ): Promise<PaymentResult | null> {
      const params = new URLSearchParams({
        contactEmail: proof.contactEmail,
        deliveryPostcode: proof.deliveryPostcode,
      });
      return get<PaymentResult>(`/payments/${encodeURIComponent(orderNumber)}`, params);
    },

    // ── Account ─────────────────────────────────────────────────────────────

    async accountProfile(): Promise<AccountProfile | null> {
      try {
        return await account<AccountProfile | null>('/account/me', 'GET');
      } catch (error) {
        if (error instanceof AccountError && error.kind === 'NOT_SIGNED_IN') return null;
        throw error;
      }
    },

    // Akademi types `register` as name and email in, nothing out. This host takes the password
    // too and answers a SignUpOutcome, which `signUp` in @commerce/site-builder-blocks reads. The
    // cast goes when Akademi's contract says so.
    register: register as unknown as CommerceSource['register'],

    async activateAccount(request: AccountActivationRequest): Promise<AccountProfile | null> {
      return account<AccountProfile | null>('/account/activate', 'POST', request);
    },

    async resendActivation(email: string): Promise<void> {
      await account<unknown>('/account/activation-code', 'POST', { email });
    },

    async signIn(request: SignInRequest): Promise<AccountProfile> {
      const profile = await account<AccountProfile>('/account/sign-in', 'POST', request);
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('storefront-account-changed'));
      return profile;
    },

    async signOut(): Promise<void> {
      await account<unknown>('/account/sign-out', 'POST', {});
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('storefront-account-changed'));
    },

    async forgotPassword(email: string): Promise<void> {
      await account<unknown>('/account/forgot-password', 'POST', { email });
    },

    async resetPassword(request: PasswordResetRequest): Promise<void> {
      await account<unknown>('/account/reset-password', 'POST', request);
    },

    async updateAccountProfile(profile: { firstName: string; lastName: string }): Promise<AccountProfile> {
      return account<AccountProfile>('/account/me', 'PUT', profile);
    },

    async requestEmailChange(request: EmailChangeRequest): Promise<void> {
      await account<unknown>('/account/email-change', 'POST', request);
    },

    async confirmEmailChange(token: string): Promise<void> {
      await account<unknown>('/account/email-change/confirm', 'POST', { token });
    },

    async accountOrders(page = 0, size = 20): Promise<AccountOrderSummary[]> {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      return (await account<AccountOrderSummary[]>(`/account/orders?${params}`, 'GET')) ?? [];
    },

    async accountOrder(orderNumber: string): Promise<Order | null> {
      try {
        return await account<Order>(`/account/orders/${encodeURIComponent(orderNumber)}`, 'GET');
      } catch (error) {
        if (error instanceof AccountError && error.kind === 'NOT_FOUND') return null;
        throw error;
      }
    },

    async accountAddresses(): Promise<AccountAddress[]> {
      return (await account<AccountAddress[]>('/account/addresses', 'GET')) ?? [];
    },

    async saveAccountAddress(address: AccountAddressInput): Promise<AccountAddress[]> {
      return (await account<AccountAddress[]>('/account/addresses', 'POST', address)) ?? [];
    },

    async updateAccountAddress(id: number, address: AccountAddressInput): Promise<AccountAddress[]> {
      return (await account<AccountAddress[]>(`/account/addresses/${id}`, 'PUT', address)) ?? [];
    },

    async removeAccountAddress(id: number): Promise<AccountAddress[]> {
      return (await account<AccountAddress[]>(`/account/addresses/${id}`, 'DELETE')) ?? [];
    },

    async makeDefaultAccountAddress(id: number): Promise<AccountAddress[]> {
      return (await account<AccountAddress[]>(`/account/addresses/${id}/default`, 'POST', {})) ?? [];
    },

    // ── Trade accounts ──────────────────────────────────────────────────────

    tradeApplicationsEnabled,

    async submitTradeApplication(
      input: TradeApplicationRequest,
      documents: TradeDocumentUpload[] = [],
    ): Promise<TradeApplicationStatus> {
      if (documents.length === 0) return account<TradeApplicationStatus>('/trade-applications', 'POST', input);
      // With documents, one multipart form: the application as JSON, then a part per file named
      // for what it proves. The browser writes the boundary, so no Content-Type is set here.
      const form = new FormData();
      form.append('application', new Blob([JSON.stringify(input)], { type: 'application/json' }));
      for (const document of documents) form.append(`document_${document.type}`, document.file, document.file.name);
      return account<TradeApplicationStatus>('/trade-applications', 'POST', form);
    },

    async tradeApplicationRequirements(): Promise<TradeRequirements> {
      return account<TradeRequirements>('/trade-applications/requirements', 'GET');
    },

    // The profile's `trade` is one of three things (contract §2): an approved account has `band`
    // and no `status`, an application has `status` and no `band`, and anything else is null.
    async myTradeApplication(): Promise<TradeApplicationStatus | null> {
      const trade = (await standing())?.trade;
      return trade && typeof trade.status === 'string' ? (trade as unknown as TradeApplicationStatus) : null;
    },

    async myTradeAccount(): Promise<TradeAccountSummary | null> {
      const trade = (await standing())?.trade;
      return trade && typeof trade.band === 'string' && trade.status === undefined
        ? (trade as unknown as TradeAccountSummary)
        : null;
    },

    async uploadTradeDocuments(documents: TradeDocumentUpload[]): Promise<TradeApplicationStatus> {
      return account<TradeApplicationStatus>('/trade-applications/mine/documents', 'POST', filesForm(documents, 'document_'));
    },

    // ── Installers ──────────────────────────────────────────────────────────

    async installerApplicationRequirements(): Promise<InstallerRequirements> {
      return account<InstallerRequirements>(`/installer-applications/requirements${languageQuery()}`, 'GET');
    },

    async submitInstallerApplication(
      input: InstallerApplicationRequest,
      certificates: InstallerCertificateUpload[],
    ): Promise<InstallerApplicationStatus> {
      // One multipart form: the application as JSON, then a part per certificate named for its
      // policy type. The browser writes the boundary.
      const form = new FormData();
      form.append('application', new Blob([JSON.stringify(input)], { type: 'application/json' }));
      for (const certificate of certificates) {
        form.append(`certificate_${certificate.type}`, certificate.file, certificate.file.name);
      }
      return account<InstallerApplicationStatus>('/installer-applications', 'POST', form);
    },

    async installerSelf(token: string): Promise<InstallerSelfView> {
      return account<InstallerSelfView>(`/installers/self${languageQuery()}`, 'GET', undefined, linkHeader(token));
    },

    async signInstallerAgreement(token: string, signing: InstallerSigning): Promise<InstallerSelfView> {
      const language = languageQuery().slice('?language='.length);
      return account<InstallerSelfView>(
        '/installers/self/agreement',
        'POST',
        { ...signing, ...(language ? { language } : {}) },
        linkHeader(token),
      );
    },

    async renewInstallerPolicy(token: string, policy: InstallerPolicyInput, certificate: File): Promise<InstallerSelfView> {
      const form = new FormData();
      form.append('policy', new Blob([JSON.stringify(policy)], { type: 'application/json' }));
      form.append('certificate', certificate, certificate.name);
      return account<InstallerSelfView>(`/installers/self/policies${languageQuery()}`, 'POST', form, linkHeader(token));
    },

    async uploadInstallerCertificates(certificates: InstallerCertificateUpload[]): Promise<InstallerApplicationStatus> {
      return account<InstallerApplicationStatus>(
        '/installer-applications/mine/certificates',
        'POST',
        filesForm(certificates, 'certificate_'),
      );
    },

    // Where an installer stands is the profile's `installer` (contract §5). Nobody signed in is
    // `NOT_SIGNED_IN` and a shopper who is neither installer nor applicant `NOT_INSTALLER`, the
    // refusals the removed `/installers/me` gave, so the blocks read it as before.
    async installerMe(): Promise<InstallerMe> {
      const profile = await standing();
      if (!profile) throw new AccountError('NOT_SIGNED_IN', 401);
      if (!profile.installer) throw new AccountError('NOT_INSTALLER' as AccountErrorKind, 404);
      return profile.installer as unknown as InstallerMe;
    },

    async saveInstallerAreas(areas: string[]): Promise<InstallerMe> {
      const profile = await account<StandingProfile>('/account/me', 'PUT', { installerAreas: areas });
      if (!profile?.installer) throw new AccountError('NOT_INSTALLER' as AccountErrorKind, 404);
      return profile.installer as unknown as InstallerMe;
    },

    async installerJobs(): Promise<InstallerJob[]> {
      return (await account<InstallerJob[] | null>('/installers/me/jobs', 'GET')) ?? [];
    },

    async quoteInstallerJob(jobId: number, quote: InstallerQuoteRequest): Promise<InstallerJob> {
      return account<InstallerJob>(`/installers/me/jobs/${Math.trunc(jobId)}/quote`, 'POST', quote);
    },

    async withdrawInstallerQuote(jobId: number): Promise<InstallerJob> {
      return account<InstallerJob>(`/installers/me/jobs/${Math.trunc(jobId)}/withdraw`, 'POST', {});
    },

    async collectionPoints(): Promise<CollectionPoint[]> {
      return (await get<CollectionPoint[]>('/collection-points')) ?? [];
    },

    async productReviews(productId: number, page = 0, size = 5): Promise<ProductReviewPage | null> {
      const params = new URLSearchParams({
        productId: String(productId),
        page: String(page),
        size: String(size),
      });
      try {
        return await account<ProductReviewPage>(`/reviews?${params.toString()}`, 'GET');
      } catch (error) {
        if (error instanceof AccountError && error.kind === 'NOT_FOUND') return null;
        throw error;
      }
    },

    async submitReview(submission: ReviewSubmission): Promise<ReviewReceipt> {
      return account<ReviewReceipt>('/reviews', 'POST', submission);
    },

    async requestReturn(input: ReturnRequestInput): Promise<OrderReturn> {
      const { proof, ...request } = input;
      // One key per press of the button: a double-click or a dropped response is one return, while
      // asking again after a refusal is a new request.
      return account<OrderReturn>(
        '/returns',
        'POST',
        {
          ...request,
          ...(proof ? { contactEmail: proof.contactEmail, deliveryPostcode: proof.deliveryPostcode } : {}),
        },
        { 'Idempotency-Key': attemptKey() },
      );
    },

    async trackOrder(orderNumber: string, proof: OrderLookup): Promise<Order | null> {
      const params = new URLSearchParams({
        contactEmail: proof.contactEmail,
        deliveryPostcode: proof.deliveryPostcode,
      });
      // 404 covers both "no such order" and "proofs do not match", by design.
      // Whatever the reason, the caller is told the same thing.
      return get<Order>(`/orders/${encodeURIComponent(orderNumber)}`, params);
    },
  };
}

/**
 * Refusals this shop's server names that Akademi's `AccountErrorKind` does not list yet. A form
 * reads a kind by name (`commerce.account.errors.<kind>`), so these read as plainly as the rest;
 * they belong in Akademi's union, and the cast in {@link accountErrorKind} covers them until then.
 */
const SHOP_ACCOUNT_KINDS = [
  'PASSWORDS_MISMATCH',
  'APPLICATION_REFUSED',
  'APPLICATION_OPEN',
  'ALREADY_TRADE',
  'INVALID_FILE',
  'ALREADY_INSTALLER',
  'NO_AGREEMENT',
  'AGREEMENT_CHANGED',
  'NOT_INSTALLER',
  'CONFLICT',
] as const;

const ACCOUNT_KINDS: ReadonlySet<string> = new Set<string>([
  ...SHOP_ACCOUNT_KINDS,
  'INVALID_INPUT',
  'EMAIL_TAKEN',
  'INVALID_CREDENTIALS',
  'NOT_SIGNED_IN',
  'INVALID_RESET',
  'INVALID_ACTIVATION',
  'WEAK_PASSWORD',
  'PASSWORD_REUSED',
  'REGISTRATION_UNAVAILABLE',
  'NOT_FOUND',
  'INVALID_LINK',
  'RATE_LIMITED',
  'UNAVAILABLE',
] satisfies (AccountErrorKind | (typeof SHOP_ACCOUNT_KINDS)[number])[]);

function accountErrorKind(
  status: number,
  payload: { developerMessage?: string; code?: string | number },
): AccountErrorKind {
  const named = [payload.developerMessage, payload.code].find(
    (value): value is string => typeof value === 'string' && ACCOUNT_KINDS.has(value),
  );
  if (named) return named as AccountErrorKind;
  if (status === 429) return 'RATE_LIMITED';
  if (status === 401) return 'NOT_SIGNED_IN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 400 || status === 422) return 'INVALID_INPUT';
  return 'UNAVAILABLE';
}
